import './styles/graphic.scss';
import { csv } from 'd3-fetch';
import { sum } from 'd3-array';
import csvData from './data/reps-data.csv';

// Import images
import carsonImg from './img/carson.png';
import casarImg from './img/casar.png';
import cleaverImg from './img/cleaver.png';
import cuellarImg from './img/cuellar.png';
import davisImg from './img/davis.png';
import gonzalezImg from './img/gonzalez.png';
import greenImg from './img/green.png';
import johnsonImg from './img/johnson.png';
import kapturImg from './img/kaptur.png';
import landsmanImg from './img/landsman.png';
import mrvanImg from './img/mrvan.png';
import scrollImg from './img/scroll.svg';

// Map rep names to images
const imageMap = {
  'André Carson': carsonImg,
  'Greg Casar': casarImg,
  'Emanuel Cleaver': cleaverImg,
  'Henry Cuellar': cuellarImg,
  'Don Davis': davisImg,
  'Vicente Gonzalez': gonzalezImg,
  'Al Green': greenImg,
  'Julie Johnson': johnsonImg,
  'Marcy Kaptur': kapturImg,
  'Greg Landsman': landsmanImg,
  'Frank Mrvan': mrvanImg,
};

export default async function (props, container, isMobile) {
  const {
    initialWidth,
    // initialHeight,
  } = props;

  // Load CSV data
  const data = await csv(csvData);

  console.log('Loaded data:', data);

  if (!data || data.length === 0) {
    container.append('p').text('Error loading data');
    return;
  }

  // Process data to categorize by caucus
  const processedData = data.map((d) => {
    let caucusCategory = 'OTHER';
    let caucusLabel = 'Other';

    if (d.Caucus && d.Caucus.includes('Black Caucus')) {
      caucusCategory = 'BLACK';
      caucusLabel = 'Congressional Black Caucus';
    } else if (d.Caucus && d.Caucus.includes('Hispanic Caucus')) {
      caucusCategory = 'HISPANIC';
      caucusLabel = 'Congressional Hispanic Caucus';
    }

    return {
      ...d,
      caucusCategory,
      caucusLabel,
      redrawParty: d['Redrawn-by'] === 'R' ? 'GOP' : 'Dem.',
    };
  });

  // Count by caucus
  const caucusCounts = {
    BLACK: processedData.filter(d => d.caucusCategory === 'BLACK').length,
    OTHER: processedData.filter(d => d.caucusCategory === 'OTHER').length,
    HISPANIC: processedData.filter(d => d.caucusCategory === 'HISPANIC').length,
  };

  const total = sum(Object.values(caucusCounts));

  // Colors
  const colors = {
    BLACK: '#68b1a0',
    HISPANIC: '#7d4ea5',
    OTHER: '#6E6E6E',
  };

  const caucusDisplay = {
    BLACK: 'CONGRESSIONAL BLACK CAUCUS',
    HISPANIC: 'CONGRESSIONAL HISPANIC CAUCUS',
    OTHER: 'OTHER',
  };

  // Create bar chart section
  const barSection = container.append('div')
    .attr('class', 'bar-section');

  const barHeader = barSection.append('div')
    .attr('class', 'bar-header');

  barHeader.append('h3')
    .attr('class', 'section-title')
    .text('Representatives targeted in redrawn maps, by caucus membership');

  const barContainer = barSection.append('div')
    .attr('class', 'bar-container');

  // Create stacked bar
  const barData = [
    {
      category: 'BLACK', barWidth: caucusCounts.BLACK, displayCount: caucusCounts.BLACK, label: 'Congressional Black Caucus', color: colors.BLACK, opacity: 1,
    },
    {
      category: 'HISPANIC', barWidth: caucusCounts.HISPANIC, displayCount: caucusCounts.HISPANIC, label: 'Congressional Hispanic Caucus', color: colors.HISPANIC, opacity: 1,
    },
    {
      category: 'TOTAL', barWidth: caucusCounts.OTHER, displayCount: total, label: 'Total', color: '#333333', opacity: 0.2,
    },
  ];

  const stackedBar = barContainer.append('div')
    .attr('class', 'stacked-bar');

  barData.forEach((d) => {
    const percentage = (d.barWidth / total) * 100;
    stackedBar.append('div')
      .attr('class', 'bar-segment')
      .style('width', `${percentage}%`)
      .style('background-color', d.color)
      .style('opacity', d.opacity);
  });

  // Create labels below bar
  const labelsContainer = barContainer.append('div')
    .attr('class', 'bar-labels');

  // Calculate cumulative positions
  let cumulativePercent = 0;

  barData.forEach((d) => {
    const labelDiv = labelsContainer.append('div')
      .attr('class', `bar-label ${d.category === 'TOTAL' ? 'bar-label-right' : ''}`)
      .style('left', d.category === 'TOTAL' ? 'auto' : `${cumulativePercent}%`)
      .style('right', d.category === 'TOTAL' ? '0' : 'auto');

    labelDiv.append('span')
      .attr('class', 'label-count')
      .text(d.displayCount);

    labelDiv.append('span')
      .attr('class', 'label-text')
      .text(d.label);

    cumulativePercent += (d.barWidth / total) * 100;
  });

  // Scroll hint below bar
  const scrollRow = barSection.append('div')
    .attr('class', 'scroll-hint-row');

  scrollRow.append('img')
    .attr('class', 'scroll-hint')
    .attr('src', scrollImg)
    .attr('alt', 'Scroll down');

  scrollRow.append('span')
    .attr('class', 'scroll-text')
    .text('Scroll down');

  // Create cards section
  const cardsSection = container.append('div')
    .attr('class', 'cards-section');

  // Add opacity gradient
  const cardsWrapper = cardsSection.append('div')
    .attr('class', 'cards-wrapper');

  const cardsGrid = cardsWrapper.append('div')
    .attr('class', 'cards-grid');

  // Sort by caucus, then alphabetically by last name
  const caucusOrder = { BLACK: 0, HISPANIC: 1, OTHER: 2 };
  const getLastName = (name = '') => {
    const parts = name.trim().split(/\s+/);
    return parts.length ? parts[parts.length - 1].toLowerCase() : '';
  };
  const sortedData = processedData.sort((a, b) => {
    const caucusDiff = caucusOrder[a.caucusCategory] - caucusOrder[b.caucusCategory];
    if (caucusDiff !== 0) return caucusDiff;
    return getLastName(a.Rep).localeCompare(getLastName(b.Rep));
  });

  // Create cards for each rep
  sortedData.forEach((rep) => {
    const card = cardsGrid.append('div')
      .attr('class', 'rep-card');

    // Top colored bar
    card.append('div')
      .attr('class', 'card-top-bar')
      .style('background-color', colors[rep.caucusCategory])
      .style('opacity', rep.caucusCategory === 'OTHER' ? 0.2 : 1);

    const cardContent = card.append('div')
      .attr('class', 'card-content');

    // Rep info header
    const repHeader = cardContent.append('div')
      .attr('class', 'rep-header');

    // Placeholder image
    const repImage = repHeader.append('div')
      .attr('class', 'rep-image');

    const imageSrc = imageMap[rep.Rep];
    if (imageSrc) {
      repImage.append('img')
        .attr('src', imageSrc)
        .attr('alt', rep.Rep || 'Representative');
    } else {
      // Fallback placeholder
      const initials = rep.Rep ? rep.Rep.split(' ').map(n => n[0]).join('') : '?';
      repImage.append('img')
        .attr('src', `https://via.placeholder.com/80x80?text=${initials}`)
        .attr('alt', rep.Rep || 'Representative');
    }

    const repInfo = repHeader.append('div')
      .attr('class', 'rep-info')
      .attr('id', rep.caucusCategory === 'OTHER' ? 'other' : '');

    repInfo.append('div')
      .attr('class', 'rep-name')
      .html(`${rep.Rep} <span class="party">(${rep['Rep-party']})</span>`);

    repInfo.append('div')
      .attr('class', 'rep-district')
      .text(`District ${rep['Old-District']}, ${rep.State}`);

    // Caucus info
    const caucusInfo = cardContent.append('div')
      .attr('class', 'caucus-info');

    if (rep.caucusCategory !== 'OTHER') {
      caucusInfo.append('span')
        .attr('class', 'caucus-badge')
        .style('background-color', colors[rep.caucusCategory])
        .text(caucusDisplay[rep.caucusCategory] || rep.caucusCategory);
    }
  });

  // Keep to two rows
  const updateFadeAtBottom = () => {
    const gridEl = cardsGrid.node();
    if (!gridEl) return;
    const atBottom = gridEl.scrollTop + gridEl.clientHeight >= gridEl.scrollHeight - 1;
    cardsWrapper.classed('fade-hidden', atBottom);
  };

  const clampToTwoRows = () => {
    const gridEl = cardsGrid.node();
    if (!gridEl) return;

    const cardNodes = Array.from(gridEl.querySelectorAll('.rep-card'));
    if (!cardNodes.length) return;

    // Determine row positions by rounded top offsets
    const rowTops = [...new Set(cardNodes.map((card) => {
      const { top } = card.getBoundingClientRect();
      return Math.round(top * 100) / 100;
    }))].sort((a, b) => a - b);

    if (rowTops.length < 2) {
      gridEl.style.maxHeight = '';
      gridEl.style.overflowY = 'visible';
      return;
    }

    const secondRowTop = rowTops[1];
    const gridTop = Math.round(gridEl.getBoundingClientRect().top * 100) / 100;

    const secondRowHeights = cardNodes
      .filter(card => Math.round(card.getBoundingClientRect().top * 100) / 100 === secondRowTop)
      .map(card => card.getBoundingClientRect().height);

    const secondRowHeight = secondRowHeights.length
      ? Math.max(...secondRowHeights)
      : cardNodes[0].getBoundingClientRect().height;

    const desiredHeight = (secondRowTop - gridTop) + secondRowHeight;
    gridEl.style.maxHeight = `${desiredHeight}px`;
    gridEl.style.overflowY = 'auto';
    updateFadeAtBottom();
  };

  // Run after layout settles
  setTimeout(() => {
    clampToTwoRows();
    const gridEl = cardsGrid.node();
    if (gridEl) {
      gridEl.addEventListener('scroll', updateFadeAtBottom);
    }
    if (window.pymChild) {
      window.pymChild.sendHeight();
    }
  }, 0);

  // Detect parent URL (with retry logic for Pym initialization)
  const detectParentUrl = () => {
    const checkParentUrl = () => {
      if (window.pymChild && window.pymChild.parentUrl) {
        console.log('Parent URL (from Pym):', window.pymChild.parentUrl);
        return window.pymChild.parentUrl;
      }
      if (document.referrer) {
        console.log('Parent URL (from referrer):', document.referrer);
        return document.referrer;
      }
      console.log('Parent URL: Not available (standalone mode)');
      return null;
    };

    // Try immediately
    let parentUrl = checkParentUrl();

    // If not available, try again after Pym initializes
    if (!parentUrl) {
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 100);
      setTimeout(() => {
        parentUrl = checkParentUrl();
      }, 500);
    }

    return parentUrl;
  };

  detectParentUrl();

  // Check if parent URL contains specific strings and adjust background
  setTimeout(() => {
    const currentParentUrl = (window.pymChild && window.pymChild.parentUrl) || document.referrer;
    const useGreyBackground = currentParentUrl && (
      currentParentUrl.includes('state-redistricting-maps-vis')
      // || currentParentUrl.includes('localhost')
      || currentParentUrl.includes('ix-cnn-io-stage')
    );

    if (useGreyBackground) {
      // Inject a style tag to set grey background
      const style = document.createElement('style');
      style.innerHTML = `
        :root, html, body, #root, .container, .wrapper, .legend, .graphic {
          background-color: #f6f6f6 !important;
        }
      `;
      document.head.appendChild(style);
      console.log('Background set to grey (#f6f6f6) for:', currentParentUrl);
    } else {
      console.log('Keeping white background for:', currentParentUrl);
    }
  }, 600);
}
